import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/react';
import {
  Bell, Brain, Globe2, ScrollText, Users, Forward, KeyRound, Keyboard, LogOut, Palette, PenLine, Plug, Plus, RefreshCw, ShieldCheck, Smartphone, Trash2, UserRound, Workflow, Check, CircleAlert,
} from 'lucide-react';
import type { SharedPreferences } from '@dts/core';
import type { McpServerConfig, McpServerState } from '@/bridge/types';
import { api, errorMessage } from '@/lib/api';
import { useMe, usePrefs, useSavePrefs, useAiStatus } from '@/lib/queries';
import { useUi } from '@/lib/store';
import { Favicon } from '@/components/Avatar';
import { EditorToolbar, HtmlSourceEditor, RichEditor } from '@/components/RichEditor';
import { confirmDialog } from '@/components/Dialogs';
import { TemplateDesigner } from '@/components/TemplateDesigner';
import { DomainsPanel } from '@/components/MigrateDomain';
import { PeoplePanel } from '@/components/PeoplePanel';
import { AliasesPanel } from '@/components/AliasesPanel';
import { MailLogPanel } from '@/components/MailLogPanel';
import { ApiTokensPanel } from '@/components/ApiTokensPanel';
import { PhoneSignInDialog } from '@/components/PhoneSignIn';
import { PoweredBy } from '@/components/PoweredBy';
import { Button, Chip, cn, Input, Kbd, Modal, Textarea, Toggle } from '@/components/ui';

const TABS = [
  { id: 'account', label: 'Account', icon: <UserRound /> },
  { id: 'signatures', label: 'Signatures', icon: <PenLine /> },
  { id: 'people', label: 'People', icon: <Users /> },
  { id: 'aliases', label: 'Aliases', icon: <Forward /> },
  { id: 'domains', label: 'Domains', icon: <Globe2 /> },
  { id: 'maillog', label: 'Mail log', icon: <ScrollText /> },
  { id: 'tokens', label: 'API tokens', icon: <KeyRound /> },
  { id: 'branding', label: 'Brand templates', icon: <Palette /> },
  { id: 'ai', label: 'AI assistant', icon: <Brain /> },
  { id: 'crm', label: 'CRM & MCP', icon: <Workflow /> },
  { id: 'senders', label: 'Safe & blocked', icon: <ShieldCheck /> },
  { id: 'appearance', label: 'Appearance', icon: <Palette /> },
  { id: 'notifications', label: 'Notifications', icon: <Bell /> },
  { id: 'shortcuts', label: 'Shortcuts', icon: <Keyboard /> },
];

export function Settings({ tab, onLogout }: { tab?: string; onLogout: () => void }) {
  const setView = useUi((s) => s.setView);
  const active = tab ?? 'account';
  return (
    <div className="flex h-full min-h-0">
      <div className="w-[230px] shrink-0 border-r border-line p-3">
        <h1 className="page-title mx-2 mb-3 mt-1 text-[22px] font-semibold tracking-tight">Settings</h1>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setView({ kind: 'settings', tab: t.id })}
            className={cn('nav-item relative flex h-9 w-full items-center gap-3 rounded-xl px-2.5 text-[13.5px] font-medium transition [&_svg]:size-4', active === t.id ? 'text-fg' : 'text-muted hover:text-fg')}
          >
            {active === t.id && <motion.span layoutId="settings-pill" className="nav-pill absolute inset-0 rounded-xl bg-line-strong/80" />}
            <span className="relative">{t.icon}</span>
            <span className="relative">{t.label}</span>
          </button>
        ))}
      </div>
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className={cn('mx-auto px-10 py-8', active === 'branding' || active === 'domains' ? 'max-w-[1240px]' : 'max-w-[720px]')}>
          {active === 'account' && <AccountTab onLogout={onLogout} />}
          {active === 'signatures' && <SignaturesTab />}
          {active === 'ai' && <AiTab />}
          {active === 'domains' && <DomainsPanel />}
          {active === 'people' && <PeoplePanel />}
          {active === 'aliases' && <AliasesPanel />}
          {active === 'maillog' && <MailLogPanel />}
          {active === 'tokens' && <ApiTokensPanel />}
          {active === 'branding' && (
            <div className="-mx-6">
              <h2 className="text-[16px] font-semibold">Brand templates</h2>
              <p className="mb-4 mt-0.5 text-[12.5px] text-muted">Describe your brand and AI designs an email layout. It's shared by every mailbox on the domain — desktop and iOS.</p>
              <TemplateDesigner />
            </div>
          )}
          {active === 'crm' && <CrmTab />}
          {active === 'senders' && <SendersTab />}
          {active === 'appearance' && <AppearanceTab />}
          {active === 'notifications' && <NotificationsTab />}
          {active === 'shortcuts' && <ShortcutsTab />}
          {/* On the web the sidebar already shows it on every screen. */}
          {window.dts.platform !== 'web' && <PoweredBy className="mt-4 flex w-full pb-2" />}
        </div>
      </div>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="eyebrow text-[16px] font-semibold">{title}</h2>
      {hint && <p className="mt-0.5 text-[12.5px] text-muted">{hint}</p>}
      <div className="glass mt-3 rounded-2xl p-5">{children}</div>
    </section>
  );
}

// ---------- Account ----------
function AccountTab({ onLogout }: { onLogout: () => void }) {
  const me = useMe().data;
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const change = async () => {
    setBusy(true);
    try {
      await api.changePassword(cur, next);
      toast.success('Password changed. Signing you in again…');
      setTimeout(onLogout, 1200);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Section title="Profile">
        <div className="text-[15px] font-semibold">{me?.user.displayName}</div>
        <div className="text-[13px] text-muted">{me?.user.email} · {me?.user.role}</div>
        <div className="mt-4 space-y-1.5">
          <div className="text-[11.5px] font-semibold uppercase tracking-wider text-faint">Your addresses</div>
          {me?.addresses.map((a) => (
            <div key={a.email} className="flex items-center gap-2.5 text-[13.5px]">
              <Favicon domain={a.domain} size={16} />
              {a.email}
              {!a.enabled && <Chip>disabled</Chip>}
            </div>
          ))}
        </div>
      </Section>
      <Section title="Change password" hint="Signs out your other sessions.">
        <div className="grid grid-cols-2 gap-3">
          <Input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} />
          <Input type="password" placeholder="New password" value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        <Button className="mt-3" variant="primary" loading={busy} disabled={!cur || next.length < 8} onClick={change} icon={<KeyRound className="size-4" />}>Update password</Button>
      </Section>
      <TwoStepSection />
      <PhoneSignInSection />
      <UpdateSection />
      <Button variant="danger" icon={<LogOut className="size-4" />} onClick={onLogout}>Sign out</Button>
    </>
  );
}

/** Two-step verification is always on; this shows its status and replaces recovery codes. */
function TwoStepSection() {
  const status = useQuery({ queryKey: ['mfa-status'], queryFn: () => api.mfaStatus() });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const regenerate = async () => {
    setBusy(true);
    try {
      setCodes(await api.regenerateRecoveryCodes(code));
      setCode('');
      void status.refetch();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const s = status.data;
  const text = codes ? `DTS Mail recovery codes\nEach code works once, in place of your authenticator app.\n\n${codes.join('\n')}\n` : '';
  return (
    <Section title="Two-step verification" hint="Required for every account. Signing in asks for a code from your authenticator app.">
      <div className="flex items-center gap-2.5 text-[13.5px]">
        <ShieldCheck className="size-4 text-ok" />
        <span className="flex-1">
          {s?.enabled ? `On (authenticator app)${s.enrolledUtc ? ` since ${new Date(s.enrolledUtc).toLocaleDateString()}` : ''}` : 'Set up at your next sign-in'}
        </span>
        {s?.enabled && <Chip color={s.recoveryCodesLeft <= 2 ? '#f43f5e' : undefined}>{s.recoveryCodesLeft} recovery code{s.recoveryCodesLeft === 1 ? '' : 's'} left</Chip>}
      </div>
      {codes ? (
        <div className="mt-4">
          <div className="mb-2 text-[12.5px] text-muted">Your new recovery codes. The old ones no longer work. Save these now; they won't be shown again.</div>
          <div className="selectable grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-xl bg-bg/60 px-4 py-3 font-mono text-[14px] tracking-wider">{codes.map((c) => <span key={c}>{c}</span>)}</div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" variant="soft" onClick={() => (navigator.clipboard.writeText(text), toast.success('Recovery codes copied'))}>Copy</Button>
            <Button size="sm" variant="soft" onClick={() => void window.dts.files.saveText('dts-mail-recovery-codes.txt', text)}>Download</Button>
            <Button size="sm" variant="ghost" onClick={() => setCodes(null)}>Done</Button>
          </div>
        </div>
      ) : s?.enabled ? (
        <div className="mt-4 flex items-center gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Code from your app" inputMode="numeric" autoComplete="one-time-code" className="w-44 font-mono tracking-widest" />
          <Button variant="soft" loading={busy} disabled={code.length !== 6} onClick={regenerate}>Get new recovery codes</Button>
        </div>
      ) : null}
    </Section>
  );
}

/** Signs in the iOS app by QR code, so the phone needs neither the password nor an authenticator code. */
function PhoneSignInSection() {
  const [open, setOpen] = useState(false);
  return (
    <Section title="Sign in on your phone" hint="Scan a QR code with the DTS Mail iOS app instead of typing your password and a code.">
      <div className="flex items-center gap-3">
        <Smartphone className="size-4 text-muted" />
        <span className="flex-1 text-[13.5px]">You approve the phone here after it scans the code.</span>
        <Button variant="soft" onClick={() => setOpen(true)}>Show QR code</Button>
      </div>
      <PhoneSignInDialog open={open} onClose={() => setOpen(false)} />
    </Section>
  );
}

function UpdateSection() {
  const [s, setS] = useState<Awaited<ReturnType<typeof window.dts.updates.state>>>({ state: 'idle' });
  useEffect(() => {
    void window.dts.updates.state().then(setS);
    return window.dts.updates.onState(setS);
  }, []);
  const text =
    s.state === 'disabled' ? 'The web app is always the latest version. Reload the page to pick up a new release.'
    : s.state === 'checking' ? 'Checking for updates…'
    : s.state === 'none' ? 'You have the latest version.'
    : s.state === 'available' ? `Version ${s.version} found, downloading…`
    : s.state === 'downloading' ? `Downloading ${s.version}… ${s.percent ?? 0}%`
    : s.state === 'ready' ? `Version ${s.version} is ready to install.`
    : s.state === 'error' ? `Update check failed: ${s.message}`
    : 'Updates are checked automatically at startup.';
  return (
    <Section title="App version" hint={`DTS Mail ${window.dts.version} · ${window.dts.platform}`}>
      <div className="flex items-center gap-3">
        <span className="flex-1 text-[13px] text-muted">{text}</span>
        {s.state === 'ready'
          ? <Button variant="primary" onClick={() => window.dts.updates.install()}>Restart to update</Button>
          : s.state === 'disabled'
            ? <Button variant="soft" onClick={() => window.dts.updates.install()}>Reload</Button>
            : <Button variant="soft" disabled={s.state === 'checking'} onClick={() => window.dts.updates.check()}>Check now</Button>}
      </div>
    </Section>
  );
}

// ---------- Signatures (HTML editor) ----------
function SignaturesTab() {
  const me = useMe().data;
  const prefs = usePrefs().data;
  const save = useSavePrefs();
  const identities = ['*', ...(me?.addresses.map((a) => a.email.toLowerCase()) ?? [])];
  const [which, setWhich] = useState('*');
  const [html, setHtml] = useState('');
  const [rev, setRev] = useState(0);
  const [mode, setMode] = useState<'rich' | 'html'>('rich');
  const [editor, setEditor] = useState<Editor | null>(null);
  useEffect(() => {
    setHtml(prefs?.signatures?.[which] ?? '');
    setRev((r) => r + 1);
  }, [which, prefs?.signatures]);
  return (
    <>
      <Section title="Signatures" hint="Added to new messages and replies. Design it visually or paste HTML from a signature generator.">
        <div className="mb-3 flex flex-wrap gap-1.5">
          {identities.map((i) => (
            <Chip key={i} active={which === i} onClick={() => setWhich(i)}>{i === '*' ? 'Default (all addresses)' : i}</Chip>
          ))}
        </div>
        <div className="overflow-hidden rounded-xl border border-line bg-bg/40">
          <div className="border-b border-line px-2 py-1.5">
            <EditorToolbar editor={editor} mode={mode} onModeChange={(m) => (m === 'rich' && setRev((r) => r + 1), setMode(m))} />
          </div>
          {mode === 'html' ? (
            <HtmlSourceEditor html={html} onChange={setHtml} minHeight={200} />
          ) : (
            <div className="flex h-[200px] flex-col"><RichEditor html={html} rev={rev} onChange={setHtml} onReady={setEditor} placeholder="—\nYour Name · Title · Company" /></div>
          )}
        </div>
        <div className="mt-3 flex gap-2">
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate({ signatures: { ...(prefs?.signatures ?? {}), [which]: html } }, { onSuccess: () => toast.success('Signature saved') })}>Save signature</Button>
          {prefs?.signatures?.[which] && (
            <Button variant="ghost" onClick={() => {
              const s = { ...(prefs.signatures ?? {}) };
              delete s[which];
              save.mutate({ signatures: s });
            }}>Remove</Button>
          )}
        </div>
      </Section>
      <Section title="Default sending address">
        <div className="flex flex-wrap gap-1.5">
          {(me?.addresses ?? []).map((a) => (
            <Chip key={a.email} active={(prefs?.defaultFrom ?? me?.addresses[0]?.email) === a.email} onClick={() => save.mutate({ defaultFrom: a.email })}>{a.email}</Chip>
          ))}
        </div>
      </Section>
    </>
  );
}

// ---------- AI ----------
function AiTab() {
  const prefs = usePrefs().data;
  const save = useSavePrefs();
  const status = useAiStatus().data;
  const local = useUi((s) => s.local);
  const setLocal = useUi((s) => s.setLocal);
  const [form, setForm] = useState<NonNullable<SharedPreferences['ai']>>({});
  useEffect(() => setForm(prefs?.ai ?? {}), [prefs?.ai]);
  const set = (patch: Partial<NonNullable<SharedPreferences['ai']>>) => setForm({ ...form, ...patch });
  return (
    <>
      <div className="ai-ring mb-8 flex items-center gap-4 rounded-2xl bg-raised/60 p-5">
        <div className="flex size-11 items-center justify-center rounded-xl ai-gradient text-white"><Brain className="size-5" /></div>
        <div className="flex-1">
          <div className="text-[15px] font-semibold">AI assistant · {status?.model ?? ''}</div>
          <div className="text-[12.5px] text-muted">Running on your Azure AI Foundry · {status?.configured ? <span className="text-ok">connected</span> : <span className="text-danger">not configured</span>}</div>
        </div>
      </div>
      <Section title="Automation">
        <Toggle label="Auto-triage new mail" hint="Categorize (needs reply, FYI, newsletter…) and write a one-line gist for each message." checked={form.autoTriage !== false} onChange={(v) => save.mutate({ ai: { autoTriage: v } })} />
        <Toggle label="Auto-summarize conversations" hint="Show the TL;DR, action items and smart replies when you open a thread." checked={form.autoSummarize !== false} onChange={(v) => save.mutate({ ai: { autoSummarize: v } })} />
        <Toggle label="Show AI gists in the message list" hint="Replace the preview text with the AI's one-line summary." checked={local.showGists} onChange={(v) => setLocal({ showGists: v })} />
      </Section>
      <Section title="Your writing style" hint="The AI uses this for every draft, on desktop and iOS.">
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[12px] text-muted">Default tone<Input value={form.tone ?? ''} onChange={(e) => set({ tone: e.target.value })} placeholder="professional and warm" /></label>
          <label className="text-[12px] text-muted">Length<Input value={form.length ?? ''} onChange={(e) => set({ length: e.target.value })} placeholder="concise" /></label>
          <label className="text-[12px] text-muted">Sign-off<Input value={form.signOff ?? ''} onChange={(e) => set({ signOff: e.target.value })} placeholder="Best," /></label>
          <label className="text-[12px] text-muted">Language<Input value={form.language ?? ''} onChange={(e) => set({ language: e.target.value })} placeholder="English" /></label>
          <label className="col-span-2 text-[12px] text-muted">About you (role, company, context)<Textarea rows={2} value={form.about ?? ''} onChange={(e) => set({ about: e.target.value })} placeholder="Founder of Dynamic Tabletop Systems; we build interactive game tables." /></label>
          <label className="col-span-2 text-[12px] text-muted">Voice sample — paste a couple of emails you wrote<Textarea rows={5} value={form.voiceSample ?? ''} onChange={(e) => set({ voiceSample: e.target.value })} /></label>
        </div>
        <Button className="mt-3" variant="primary" loading={save.isPending} onClick={() => save.mutate({ ai: form }, { onSuccess: () => toast.success('Writing style saved') })}>Save</Button>
      </Section>
    </>
  );
}

// ---------- CRM / MCP ----------
const blankServer = (): McpServerConfig => ({ id: crypto.randomUUID(), name: '', enabled: true, transport: 'http', url: '', headers: {}, args: [], env: {} });

function CrmTab() {
  const prefs = usePrefs().data;
  const save = useSavePrefs();
  const servers = useQuery({ queryKey: ['mcp'], queryFn: () => window.dts.mcp.list(), refetchInterval: 15_000 });
  const qc = useQueryClient();
  const [editing, setEditing] = useState<McpServerConfig | null>(null);
  const [instructions, setInstructions] = useState('');
  useEffect(() => setInstructions(prefs?.crm?.instructions ?? ''), [prefs?.crm?.instructions]);
  const mode = prefs?.crm?.mode ?? 'off';
  return (
    <>
      <Section title="Log emails to your CRM" hint="After you send, the AI finds the matching contact/deal through your MCP connections and records the email.">
        <div className="grid grid-cols-3 gap-2">
          {([
            ['off', 'Off', 'Never log'],
            ['ask', 'Ask first', 'Approve each CRM change'],
            ['auto', 'Automatic', 'Log without asking'],
          ] as const).map(([id, t, d]) => (
            <button key={id} onClick={() => save.mutate({ crm: { mode: id } })} className={cn('rounded-xl border p-3 text-left transition', mode === id ? 'border-accent bg-accent/10' : 'border-line hover:border-line-strong')}>
              <div className="text-[13.5px] font-semibold">{t}</div>
              <div className="text-[12px] text-muted">{d}</div>
            </button>
          ))}
        </div>
        <label className="mt-4 block text-[12px] text-muted">
          Standing instructions for the CRM agent (optional)
          <Textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Log as an 'Email' activity. Create a prospect if the sender's company isn't in the CRM." />
        </label>
        <Button size="sm" className="mt-2" variant="soft" onClick={() => save.mutate({ crm: { instructions } }, { onSuccess: () => toast.success('Saved') })}>Save instructions</Button>
      </Section>

      <Section title="MCP connections" hint="Connect any CRM that offers an MCP server (HubSpot, Salesforce, Pipedrive, your own…). Secrets are encrypted with your OS keychain.">
        <div className="space-y-2">
          {servers.data?.length === 0 && <div className="py-4 text-center text-[13px] text-muted">No connections yet.</div>}
          {servers.data?.map((s) => <ServerRow key={s.id} s={s} onEdit={() => setEditing(s)} onChanged={() => qc.invalidateQueries({ queryKey: ['mcp'] })} />)}
        </div>
        <Button className="mt-3" variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing(blankServer())}>Add connection</Button>
      </Section>
      <ServerEditor value={editing} onClose={() => setEditing(null)} onSaved={() => qc.invalidateQueries({ queryKey: ['mcp'] })} />
    </>
  );
}

function ServerRow({ s, onEdit, onChanged }: { s: McpServerState; onEdit: () => void; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex items-center gap-3 rounded-xl border border-line bg-bg/40 px-3.5 py-3">
      <Plug className={cn('size-4', s.connected ? 'text-ok' : s.error ? 'text-danger' : 'text-faint')} />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold">{s.name}</div>
        <div className="truncate text-[12px] text-muted">
          {s.connected ? `${s.tools.length} tools · ${s.tools.slice(0, 4).map((t) => t.name).join(', ')}${s.tools.length > 4 ? '…' : ''}` : s.error ?? (s.enabled ? 'Not connected yet' : 'Disabled')}
        </div>
      </div>
      <Button size="sm" variant="ghost" loading={busy} icon={<RefreshCw className="size-3.5" />} onClick={async () => {
        setBusy(true);
        await window.dts.mcp.reconnect(s.id);
        setBusy(false);
        onChanged();
      }}>Reconnect</Button>
      <Button size="sm" variant="soft" onClick={onEdit}>Edit</Button>
      <Button size="sm" variant="ghost" onClick={async () => {
        if (await confirmDialog(`Remove ${s.name}?`, undefined, { confirmLabel: 'Remove', danger: true })) {
          await window.dts.mcp.remove(s.id);
          onChanged();
        }
      }}><Trash2 className="size-3.5" /></Button>
    </div>
  );
}

function kv(text: string): Record<string, string> {
  return Object.fromEntries(text.split('\n').map((l) => l.trim()).filter((l) => l.includes(':') || l.includes('=')).map((l) => {
    const i = l.search(/[:=]/);
    return [l.slice(0, i).trim(), l.slice(i + 1).trim()];
  }));
}

function ServerEditor({ value, onClose, onSaved }: { value: McpServerConfig | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<McpServerConfig | null>(value);
  const [headers, setHeaders] = useState('');
  const [env, setEnv] = useState('');
  const [test, setTest] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF(value);
    setHeaders(Object.entries(value?.headers ?? {}).map(([k, v]) => `${k}: ${v}`).join('\n'));
    setEnv(Object.entries(value?.env ?? {}).map(([k, v]) => `${k}=${v}`).join('\n'));
    setTest(null);
  }, [value]);
  if (!f) return null;
  const full = (): McpServerConfig => ({ ...f, headers: kv(headers), env: kv(env) });
  return (
    <Modal open={!!value} onClose={onClose} title="MCP connection" width={600}>
      <div className="space-y-3 p-5">
        <label className="block text-[12px] text-muted">Name<Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="HubSpot" autoFocus /></label>
        <div className="flex gap-2">
          {(['http', 'stdio'] as const).map((t) => (
            <Chip key={t} active={f.transport === t} onClick={() => setF({ ...f, transport: t })}>{t === 'http' ? 'Remote (HTTP / SSE)' : 'Local command (stdio)'}</Chip>
          ))}
        </div>
        {f.transport === 'stdio' && window.dts.platform === 'web' && (
          <div className="rounded-xl bg-warn/10 px-3 py-2 text-[12.5px] text-warn">Local commands only run in the DTS Mail desktop app. In the browser, use a remote server that allows requests from this website.</div>
        )}
        {f.transport === 'http' ? (
          <>
            <label className="block text-[12px] text-muted">Server URL<Input value={f.url ?? ''} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://mcp.example.com/mcp" className="font-mono text-[12px]" /></label>
            <label className="block text-[12px] text-muted">Headers (one per line, e.g. Authorization: Bearer …)<Textarea rows={3} value={headers} onChange={(e) => setHeaders(e.target.value)} className="font-mono text-[12px]" /></label>
          </>
        ) : (
          <>
            <label className="block text-[12px] text-muted">Command<Input value={f.command ?? ''} onChange={(e) => setF({ ...f, command: e.target.value })} placeholder="npx" className="font-mono text-[12px]" /></label>
            <label className="block text-[12px] text-muted">Arguments (space separated)<Input value={(f.args ?? []).join(' ')} onChange={(e) => setF({ ...f, args: e.target.value.split(' ').filter(Boolean) })} placeholder="-y @hubspot/mcp-server" className="font-mono text-[12px]" /></label>
            <label className="block text-[12px] text-muted">Environment (KEY=value per line)<Textarea rows={3} value={env} onChange={(e) => setEnv(e.target.value)} className="font-mono text-[12px]" /></label>
          </>
        )}
        <Toggle label="Enabled" checked={f.enabled} onChange={(v) => setF({ ...f, enabled: v })} />
        {test && (
          <div className={cn('flex items-start gap-2 rounded-xl px-3 py-2 text-[12.5px]', test.ok ? 'bg-ok/10 text-ok' : 'bg-danger/10 text-danger')}>
            {test.ok ? <Check className="mt-0.5 size-4 shrink-0" /> : <CircleAlert className="mt-0.5 size-4 shrink-0" />}
            <span className="selectable break-all">{test.text}</span>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
        <Button variant="ghost" loading={busy} onClick={async () => {
          setBusy(true);
          const r = await window.dts.mcp.test(full());
          setBusy(false);
          setTest(r.ok ? { ok: true, text: `Connected · ${r.data.length} tools: ${r.data.map((t) => t.name).join(', ')}` } : { ok: false, text: r.error });
        }}>Test</Button>
        <Button variant="primary" disabled={!f.name.trim()} onClick={async () => {
          await window.dts.mcp.save(full());
          onSaved();
          onClose();
          toast.success(`${f.name} saved`);
        }}>Save</Button>
      </div>
    </Modal>
  );
}

// ---------- Safe / blocked ----------
function SendersTab() {
  const qc = useQueryClient();
  const safe = useQuery({ queryKey: ['list', 'safe'], queryFn: () => api.senderList('safe') });
  const block = useQuery({ queryKey: ['list', 'block'], queryFn: () => api.senderList('block') });
  const [entry, setEntry] = useState('');
  const add = async (list: 'safe' | 'block') => {
    try {
      await api.addToSenderList(list, [entry.trim()]);
      setEntry('');
      qc.invalidateQueries({ queryKey: ['list'] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const remove = async (list: 'safe' | 'block', e: string) => {
    await api.removeFromSenderList(list, e);
    qc.invalidateQueries({ queryKey: ['list'] });
  };
  return (
    <>
      <Section title="Add a sender" hint="An email address or a whole domain (covers subdomains).">
        <div className="flex gap-2">
          <Input value={entry} onChange={(e) => setEntry(e.target.value)} placeholder="name@example.com or example.com" />
          <Button variant="soft" disabled={!entry.trim()} onClick={() => add('safe')}>Always allow</Button>
          <Button variant="danger" disabled={!entry.trim()} onClick={() => add('block')}>Block</Button>
        </div>
      </Section>
      {(['safe', 'block'] as const).map((list) => {
        const q = list === 'safe' ? safe : block;
        return (
          <Section key={list} title={list === 'safe' ? 'Safe senders' : 'Blocked senders'}>
            {q.data?.length === 0 && <div className="text-[13px] text-muted">None.</div>}
            <div className="flex flex-wrap gap-1.5">
              {q.data?.map((e) => (
                <span key={e.entry} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-bg/40 py-1 pl-2 pr-1 text-[12.5px]">
                  <Favicon domain={e.entry.includes('@') ? e.entry.split('@')[1] : e.entry} size={14} />
                  {e.entry}
                  <button className="rounded-full px-1 text-faint hover:text-fg" onClick={() => void remove(list, e.entry)}>✕</button>
                </span>
              ))}
            </div>
          </Section>
        );
      })}
    </>
  );
}

// ---------- Appearance ----------
function AppearanceTab() {
  const local = useUi((s) => s.local);
  const setLocal = useUi((s) => s.setLocal);
  const hues = [275, 250, 210, 175, 145, 30, 0, 330];
  return (
    <>
      <Section title="Theme">
        <div className="flex gap-2">
          {(['dark', 'light', 'system'] as const).map((t) => <Chip key={t} active={local.theme === t} onClick={() => setLocal({ theme: t })}>{t[0].toUpperCase() + t.slice(1)}</Chip>)}
        </div>
        <div className="dark-only">
          <div className="mt-5 text-[12px] text-muted">Accent</div>
          <div className="mt-2 flex gap-2">
            {hues.map((h) => (
              <button key={h} onClick={() => setLocal({ hue: h })} className={cn('size-8 rounded-full transition hover:scale-110', local.hue === h && 'ring-2 ring-fg ring-offset-2 ring-offset-bg')} style={{ background: `linear-gradient(135deg, oklch(0.7 0.2 ${h}), oklch(0.72 0.2 ${h + 55}))` }} />
            ))}
          </div>
        </div>
        {/* Light mode is the Andor Dossier (paper, ink, one rust accent), so the accent picker applies to dark mode only. */}
        <p className="light-only mt-4 text-[12px] text-muted">Light mode uses the Dossier palette: paper, ink and a rust accent. The accent colour applies to dark mode.</p>
      </Section>
      <Section title="Layout">
        <div className="mb-2 flex gap-2">
          {(['cozy', 'compact'] as const).map((d) => <Chip key={d} active={local.density === d} onClick={() => setLocal({ density: d })}>{d === 'cozy' ? 'Cozy' : 'Compact'}</Chip>)}
        </div>
        <Toggle label="Group messages into conversations" checked={local.conversations} onChange={(v) => setLocal({ conversations: v })} />
        <Toggle label="Show sender website icons" hint="Loaded from each sender's own website." checked={local.favicons} onChange={(v) => setLocal({ favicons: v })} />
        <Toggle label="Always load remote images" hint="Off protects you from tracking pixels." checked={local.remoteImages === 'always'} onChange={(v) => setLocal({ remoteImages: v ? 'always' : 'ask' })} />
      </Section>
    </>
  );
}

function NotificationsTab() {
  const local = useUi((s) => s.local);
  const setLocal = useUi((s) => s.setLocal);
  const status = useQuery({ queryKey: ['event-status'], queryFn: () => window.dts.events.status(), refetchInterval: 5000 });
  const state = status.data?.state ?? 'stopped';
  return (
    <>
      <Section title="Live updates" hint="New mail and changes from your other devices arrive through a private Azure Storage Queue for this device.">
        <div className="flex items-center gap-3">
          <span className={cn('size-2.5 rounded-full', state === 'connected' ? 'bg-ok shadow-[0_0_10px] shadow-ok' : state === 'error' ? 'bg-danger' : 'bg-faint')} />
          <span className="flex-1 text-[13.5px]">{state === 'connected' ? 'Connected' : state === 'error' ? `Reconnecting… ${status.data?.detail ?? ''}` : 'Not running'}</span>
          <Button
            size="sm"
            variant="soft"
            onClick={async () => {
              const r = await window.dts.events.test();
              if (!r.ok) toast.error(r.error);
            }}
          >
            Send test event
          </Button>
        </div>
      </Section>
      <Section title={window.dts.platform === 'web' ? 'Browser notifications' : 'Desktop notifications'}>
        <Toggle
          label="Notify me about new mail"
          hint="A system notification when DTS Mail is in the background, a toast when it is in front. Junk is never announced."
          checked={local.notifications}
          onChange={(v) => setLocal({ notifications: v })}
        />
      </Section>
    </>
  );
}

function ShortcutsTab() {
  const rows: [string, string][] = [
    ['C', 'New message'], ['Ctrl Shift N', 'Compose with AI'], ['Ctrl K', 'Command palette'], ['Ctrl J', 'Ask your inbox'],
    ['J / K', 'Next / previous conversation'], ['R', 'Reply'], ['A', 'Reply all'], ['F', 'Forward'], ['E', 'Archive'],
    ['#', 'Delete'], ['S', 'Flag'], ['U', 'Mark unread'], ['!', 'Mark junk'], ['/', 'Search'], ['Ctrl Enter', 'Send'],
    ['Esc', 'Minimize composer'], ['Ctrl 1/2/3', 'Today / Inbox / Contacts'],
  ];
  return (
    <Section title="Keyboard shortcuts">
      <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
        {rows.map(([k, d]) => (
          <div key={k} className="flex items-center justify-between text-[13px]">
            <span className="text-muted">{d}</span>
            <span className="flex gap-1">{k.split(' ').map((x) => <Kbd key={x}>{x}</Kbd>)}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}
